// src/ContactForm.tsx
import { useEffect, useState } from "react";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { Turnstile } from "@marsidev/react-turnstile";
import * as yup from "yup";
import Box from "@mui/material/Box/index.js";
import Grid from "@mui/material/Grid/index.js";
import TextField from "@mui/material/TextField/index.js";
import Button from "@mui/material/Button/index.js";
import Typography from "@mui/material/Typography/index.js";
import { jsx, jsxs } from "react/jsx-runtime";
var validationSchema = yup.object({
  firstName: yup.string().trim().min(2, "Please enter a valid name").max(50, "Please enter a valid name").required("Please specify your first name"),
  lastName: yup.string().trim().min(2, "Please enter a valid name").max(50, "Please enter a valid name").required("Please specify your last name"),
  email: yup.string().trim().email("Please enter a valid email address").required("Email is required."),
  company: yup.string().trim().min(1, "Please enter a valid company name").max(50, "Please enter a valid company name"),
  message: yup.string().trim().max(1e3, "The message cannot contain more than 1000 characters")
});
var ContactForm = ({
  formData,
  setFormData,
  handleSubmit,
  disabled
}) => {
  const { t } = useTranslation();
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaError, setCaptchaError] = useState("");
  const [captchaKey, setCaptchaKey] = useState(0);
  const turnstileSiteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;
  const initialValues = {
    firstName: "",
    lastName: "",
    email: "",
    company: "",
    message: "",
    website: "",
    captchaToken: ""
  };
  const handleFormikSubmit = async () => {
    if (!captchaToken) {
      setCaptchaError("Please verify that you are not a robot.");
      return;
    }
    await handleSubmit();
    formik.resetForm();
    setCaptchaToken("");
    setCaptchaKey((current) => current + 1);
    setCaptchaError(
      !turnstileSiteKey ? "Captcha is not configured. Please contact support." : ""
    );
  };
  const formik = useFormik({
    initialValues: formData || initialValues,
    validationSchema,
    onSubmit: handleFormikSubmit
  });
  useEffect(() => {
    setFormData({ ...formik.values, captchaToken });
  }, [captchaToken, formik.values, setFormData]);
  useEffect(() => {
    if (!turnstileSiteKey) {
      setCaptchaError("Captcha is not configured. Please contact support.");
      return;
    }
    setCaptchaError("");
  }, [turnstileSiteKey]);
  return /* @__PURE__ */ jsxs(Box, { children: [
    /* @__PURE__ */ jsxs(Box, { style: { marginBottom: "20px" }, children: [
      /* @__PURE__ */ jsx(Typography, { color: "textSecondary", children: t("form.completeForm") }),
      /* @__PURE__ */ jsx(Typography, { color: "textSecondary", children: t("form.responseTime") })
    ] }),
    /* @__PURE__ */ jsxs("form", { onSubmit: formik.handleSubmit, children: [
      /* @__PURE__ */ jsx(
        Box,
        {
          sx: {
            position: "absolute",
            left: "-9999px",
            width: "1px",
            height: "1px",
            overflow: "hidden"
          },
          "aria-hidden": "true",
          children: /* @__PURE__ */ jsx(
            TextField,
            {
              label: "Website",
              name: "website",
              autoComplete: "off",
              tabIndex: -1,
              value: formik.values.website || "",
              onChange: formik.handleChange
            }
          )
        }
      ),
      /* @__PURE__ */ jsxs(Grid, { container: true, spacing: 4, children: [
        /* @__PURE__ */ jsxs(Grid, { item: true, container: true, xs: 12, md: 6, spacing: 4, children: [
          /* @__PURE__ */ jsx(Grid, { item: true, xs: 12, children: /* @__PURE__ */ jsx(
            TextField,
            {
              label: t("form.firstName"),
              variant: "outlined",
              name: "firstName",
              fullWidth: true,
              value: formik.values.firstName,
              onChange: formik.handleChange,
              error: formik.touched.firstName && Boolean(formik.errors.firstName),
              helperText: formik.touched.firstName && formik.errors.firstName
            }
          ) }),
          /* @__PURE__ */ jsx(Grid, { item: true, xs: 12, children: /* @__PURE__ */ jsx(
            TextField,
            {
              label: t("form.lastName"),
              variant: "outlined",
              name: "lastName",
              fullWidth: true,
              value: formik.values.lastName,
              onChange: formik.handleChange,
              error: formik.touched.lastName && Boolean(formik.errors.lastName),
              helperText: formik.touched.lastName && formik.errors.lastName
            }
          ) }),
          /* @__PURE__ */ jsx(Grid, { item: true, xs: 12, children: /* @__PURE__ */ jsx(
            TextField,
            {
              label: t("form.email"),
              variant: "outlined",
              name: "email",
              fullWidth: true,
              value: formik.values.email,
              onChange: formik.handleChange,
              error: formik.touched.email && Boolean(formik.errors.email),
              helperText: formik.touched.email && formik.errors.email
            }
          ) }),
          /* @__PURE__ */ jsx(Grid, { item: true, xs: 12, children: /* @__PURE__ */ jsx(
            TextField,
            {
              label: t("form.company"),
              variant: "outlined",
              name: "company",
              fullWidth: true,
              value: formik.values.company,
              onChange: formik.handleChange,
              error: formik.touched.company && Boolean(formik.errors.company),
              helperText: formik.touched.company && formik.errors.company
            }
          ) })
        ] }),
        /* @__PURE__ */ jsx(Grid, { item: true, xs: 12, md: 6, children: /* @__PURE__ */ jsx(
          TextField,
          {
            label: t("form.message"),
            variant: "outlined",
            name: "message",
            fullWidth: true,
            multiline: true,
            rows: 12,
            sx: {
              "& .MuiInputBase-root": {
                height: "324px"
              }
            },
            value: formik.values.message,
            onChange: formik.handleChange,
            error: formik.touched.message && Boolean(formik.errors.message),
            helperText: formik.touched.message && formik.errors.message
          }
        ) }),
        /* @__PURE__ */ jsxs(Grid, { item: true, xs: 12, md: 6, children: [
          turnstileSiteKey && /* @__PURE__ */ jsx(Box, { sx: { marginBottom: "8px" }, children: /* @__PURE__ */ jsx(
            Turnstile,
            {
              siteKey: turnstileSiteKey,
              onSuccess: (token) => {
                setCaptchaToken(token);
                setCaptchaError("");
              },
              onExpire: () => {
                setCaptchaToken("");
                setCaptchaError("Captcha expired. Please verify again.");
              },
              onError: () => {
                setCaptchaToken("");
                setCaptchaError(
                  "Captcha failed to load. Please try again."
                );
              }
            },
            captchaKey
          ) }),
          captchaError && /* @__PURE__ */ jsx(
            Typography,
            {
              color: "error",
              variant: "body2",
              sx: { marginBottom: "12px" },
              children: captchaError
            }
          ),
          /* @__PURE__ */ jsx(
            Button,
            {
              size: "large",
              variant: "contained",
              type: "submit",
              disabled: disabled || !captchaToken || !turnstileSiteKey,
              children: t("form.sendRequest")
            }
          )
        ] })
      ] })
    ] })
  ] });
};
export {
  ContactForm
};
